'use client'

import { useQuery } from '@tanstack/react-query'

import { useAnalyticsBook } from '@/service/amberdata/analytics.hook'
import {
  fetchGexAggregate,
  fetchGreeksByExpiry,
  fetchImpliedMove,
  fetchIvPercentile,
  fetchMaxPain,
  fetchOptionsOiLeaderboard,
  fetchOptionsOiSeries,
  fetchOptionsStrikeFlow,
  fetchOptionsTradesHistory,
  fetchOptionsVolumeLeaderboard,
  fetchOptionsVolumeSeries,
} from '@/service/options-analytics.api'
import {
  exchangeSupportsAggressorSide,
  OPTIONS_OI_SERIES_MIN_TIMEFRAME_SECONDS,
  OPTIONS_STRIKE_FLOW_MIN_TIMEFRAME_SECONDS,
} from '@/service/options-analytics.types'
import { snapshotAndUpdateQueryOptions } from '@/service/snapshot-query-options'
import type {
  GetGexAggregateRequest,
  GetGreeksByExpiryRequest,
  GetImpliedMoveRequest,
  GetIvPercentileRequest,
  GetMaxPainRequest,
  GetOptionsOiLeaderboardRequest,
  GetOptionsVolumeLeaderboardRequest,
  OptionsOiSeriesRequest,
  OptionsStrikeFlowRequest,
  OptionsTradesHistoryRequest,
  OptionsVolumeSeriesRequest,
} from '@/types/generated/public_api'

export const optionsAnalyticsKeys = {
  all: ['optionsAnalytics'] as const,
  volumeSeries: (params: OptionsVolumeSeriesRequest) =>
    [
      ...optionsAnalyticsKeys.all,
      'volumeSeries',
      params.exchange,
      params.underlying,
      params.timeframe,
      params.volumeMeasure,
      params.from,
      params.to,
      params.limit,
    ] as const,
  volumeLeaderboard: (params: GetOptionsVolumeLeaderboardRequest) =>
    [
      ...optionsAnalyticsKeys.all,
      'volumeLeaderboard',
      params.exchange,
      params.underlying,
      params.volumeMeasure,
      params.groupBy,
      params.optionType,
      params.limit,
    ] as const,
  oiSeries: (params: OptionsOiSeriesRequest) =>
    [
      ...optionsAnalyticsKeys.all,
      'oiSeries',
      params.exchange,
      params.underlying,
      params.timeframe,
      params.measure,
      params.optionType,
      params.from,
      params.to,
      params.limit,
    ] as const,
  strikeFlow: (params: OptionsStrikeFlowRequest) =>
    [
      ...optionsAnalyticsKeys.all,
      'strikeFlow',
      params.exchange,
      params.underlying,
      params.timeframe,
      params.volumeMeasure,
      params.expiryMs,
      params.from,
      params.to,
      params.limit,
      params.strikeLimit,
    ] as const,
  oiLeaderboard: (params: GetOptionsOiLeaderboardRequest) =>
    [
      ...optionsAnalyticsKeys.all,
      'oiLeaderboard',
      params.exchange,
      params.underlying,
      params.measure,
      params.mode,
      params.optionType,
      params.limit,
    ] as const,
  tradesHistory: (params: OptionsTradesHistoryRequest) =>
    [
      ...optionsAnalyticsKeys.all,
      'tradesHistory',
      params.exchange,
      params.underlying,
      params.blockMode,
      params.sizeMeasure,
      params.minSize,
      params.from,
      params.to,
      params.limit,
    ] as const,
  impliedMove: (params: GetImpliedMoveRequest) =>
    [...optionsAnalyticsKeys.all, 'impliedMove', params.exchange, params.underlying] as const,
  ivPercentile: (params: GetIvPercentileRequest) =>
    [...optionsAnalyticsKeys.all, 'ivPercentile', params.exchange, params.underlying] as const,
  maxPain: (params: GetMaxPainRequest) =>
    [...optionsAnalyticsKeys.all, 'maxPain', params.exchange, params.underlying] as const,
  greeksByExpiry: (params: GetGreeksByExpiryRequest) =>
    [...optionsAnalyticsKeys.all, 'greeksByExpiry', params.exchange, params.underlying] as const,
  gexAggregate: (params: GetGexAggregateRequest) =>
    [...optionsAnalyticsKeys.all, 'gexAggregate', params.exchange, params.currency] as const,
}

export function useOptionsVolumeSeries(params: OptionsVolumeSeriesRequest, enabled = true) {
  return useQuery({
    queryKey: optionsAnalyticsKeys.volumeSeries(params),
    queryFn: ({ signal }) => fetchOptionsVolumeSeries(params, signal),
    enabled:
      enabled &&
      Boolean(params.exchange) &&
      Boolean(params.underlying) &&
      Boolean(params.timeframe) &&
      Boolean(params.volumeMeasure),
    staleTime: 10 * 1000,
  })
}

export function useOptionsVolumeLeaderboard(
  params: GetOptionsVolumeLeaderboardRequest,
  enabled = true
) {
  return useQuery({
    queryKey: optionsAnalyticsKeys.volumeLeaderboard(params),
    queryFn: ({ signal }) => fetchOptionsVolumeLeaderboard(params, signal),
    enabled:
      enabled &&
      Boolean(params.exchange) &&
      Boolean(params.underlying) &&
      Boolean(params.volumeMeasure) &&
      Boolean(params.groupBy),
    staleTime: 10 * 1000,
  })
}

export function useOptionsOiSeries(params: OptionsOiSeriesRequest, enabled = true) {
  return useQuery({
    queryKey: optionsAnalyticsKeys.oiSeries(params),
    queryFn: ({ signal }) => fetchOptionsOiSeries(params, signal),
    enabled:
      enabled &&
      Boolean(params.exchange) &&
      Boolean(params.underlying) &&
      Boolean(params.measure) &&
      params.timeframe >= OPTIONS_OI_SERIES_MIN_TIMEFRAME_SECONDS,
    staleTime: 10 * 1000,
  })
}

/**
 * Strike × time flow heatmap. Guarded on both venue and resolution so requests
 * the server would reject never leave the client: OPRA has no aggressor side
 * (BadRequest), and sub-5m buckets cover too little of the tape to read.
 */
export function useOptionsStrikeFlow(params: OptionsStrikeFlowRequest, enabled = true) {
  return useQuery({
    queryKey: optionsAnalyticsKeys.strikeFlow(params),
    queryFn: ({ signal }) => fetchOptionsStrikeFlow(params, signal),
    enabled:
      enabled &&
      Boolean(params.exchange) &&
      Boolean(params.underlying) &&
      Boolean(params.volumeMeasure) &&
      exchangeSupportsAggressorSide(params.exchange) &&
      params.timeframe >= OPTIONS_STRIKE_FLOW_MIN_TIMEFRAME_SECONDS,
    staleTime: 10 * 1000,
  })
}

export function useOptionsOiLeaderboard(params: GetOptionsOiLeaderboardRequest, enabled = true) {
  return useQuery({
    queryKey: optionsAnalyticsKeys.oiLeaderboard(params),
    queryFn: ({ signal }) => fetchOptionsOiLeaderboard(params, signal),
    enabled:
      enabled &&
      Boolean(params.exchange) &&
      Boolean(params.underlying) &&
      Boolean(params.measure) &&
      Boolean(params.mode),
    staleTime: 10 * 1000,
  })
}

// One-shot seed for the Options Trades tape: last `limit` trades within the
// server's 24h default lookback (from/to omitted on purpose). Keyed on the
// stream's server-side filter set — NOT on option_type, which is a
// client-side display filter.
export function useOptionsTradesHistory(params: OptionsTradesHistoryRequest, enabled = true) {
  return useQuery({
    queryKey: optionsAnalyticsKeys.tradesHistory(params),
    queryFn: ({ signal }) => fetchOptionsTradesHistory(params, signal),
    enabled: enabled && Boolean(params.exchange) && Boolean(params.underlying),
    ...snapshotAndUpdateQueryOptions,
    // Unlike the other snapshot+stream hooks, changing a tape filter can return
    // to a previously observed query key. The cached snapshot is not updated by
    // live frames, so it must refetch whenever that key becomes active again.
    staleTime: 0,
  })
}

// ENG-738 §7.1 — snapshot-only derived panels: no from/to, no pagination,
// not something to poll live, so staleTime matches the other snapshot hooks.

export function useImpliedMove(params: GetImpliedMoveRequest, enabled = true) {
  return useQuery({
    queryKey: optionsAnalyticsKeys.impliedMove(params),
    queryFn: ({ signal }) => fetchImpliedMove(params, signal),
    enabled: enabled && Boolean(params.exchange) && Boolean(params.underlying),
    staleTime: 10 * 1000,
  })
}

export function useIvPercentile(params: GetIvPercentileRequest, enabled = true) {
  return useQuery({
    queryKey: optionsAnalyticsKeys.ivPercentile(params),
    queryFn: ({ signal }) => fetchIvPercentile(params, signal),
    enabled: enabled && Boolean(params.exchange) && Boolean(params.underlying),
    staleTime: 10 * 1000,
  })
}

export function useMaxPain(params: GetMaxPainRequest, enabled = true) {
  return useQuery({
    queryKey: optionsAnalyticsKeys.maxPain(params),
    queryFn: ({ signal }) => fetchMaxPain(params, signal),
    enabled: enabled && Boolean(params.exchange) && Boolean(params.underlying),
    staleTime: 10 * 1000,
  })
}

export function useGreeksByExpiry(params: GetGreeksByExpiryRequest, enabled = true) {
  return useQuery({
    queryKey: optionsAnalyticsKeys.greeksByExpiry(params),
    queryFn: ({ signal }) => fetchGreeksByExpiry(params, signal),
    enabled: enabled && Boolean(params.exchange) && Boolean(params.underlying),
    staleTime: 10 * 1000,
  })
}

export function useGexAggregate(params: GetGexAggregateRequest, enabled = true) {
  const resolved = { ...params, currency: useAnalyticsBook(params.exchange, params.currency) ?? '' }
  return useQuery({
    queryKey: optionsAnalyticsKeys.gexAggregate(resolved),
    queryFn: ({ signal }) => fetchGexAggregate(resolved, signal),
    enabled: enabled && Boolean(resolved.exchange) && Boolean(resolved.currency),
    staleTime: 10 * 1000,
  })
}
